import { StandardEvents } from '@shopify/events';

const handledRequests = new WeakSet();
const buttonCycles = new WeakMap();
let activeFlight = null;

// Presentation only. The native product form owns submission, errors, cart
// updates and its accessible success announcement.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add' || !(event.target instanceof Element)) return;
  const studio = event.target.closest('.calvielle-product-studio');
  if (!studio || !event.promise || handledRequests.has(event.promise)) return;
  handledRequests.add(event.promise);

  const mainButton = event.target.querySelector('.add-to-cart-button');
  // Sticky is a sibling of the Studio content. Capture the actual button before
  // the native sticky listener resets its puppet flag when the promise settles.
  const section = event.target.closest('.shopify-section');
  const sticky = section?.querySelector('sticky-add-to-cart');
  const source = mainButton?.dataset.puppet === 'true' &&
    sticky?.dataset.productId === event.target.dataset.productId
    ? sticky.querySelector('[ref="addToCartButton"]')
    : mainButton;

  event.promise.then(async ({ detail }) => {
    if (detail?.didError !== false || !source?.isConnected) return;
    const cycle = {};
    buttonCycles.set(source, cycle);
    source.dataset.added = 'true';

    try {
      const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const flight = !reduced && playBagFlight(source);
      if (flight) {
        await flight;
        // Hold the completed check long enough to read on both touch and
        // pointer devices before the button label returns.
        await new Promise((resolve) => setTimeout(resolve, 700));
      } else {
        // A quiet check remains available with reduced motion or an offscreen
        // cart icon. Never require animation support to confirm a successful add.
        await new Promise((resolve) => setTimeout(resolve, 1500));
      }
    } finally {
      // An earlier request finishing must not clear a newer success on the same
      // button. Restore its label only after that request's feedback finishes.
      if (buttonCycles.get(source) === cycle) {
        source.removeAttribute('data-added');
        buttonCycles.delete(source);
      }
    }
  }).catch(() => { /* Native form owns errors; failures never start feedback. */ });
});

function playBagFlight(source) {
  const destination = document.querySelector('.header-actions__cart-icon');
  if (!destination) return null;
  const start = source.getBoundingClientRect();
  const end = destination.getBoundingClientRect();
  const mobile = matchMedia('(max-width: 749px), (pointer: coarse)').matches;
  if (!end.width || end.bottom < 0 || end.top > innerHeight) return null;
  if (!mobile && (start.bottom < 0 || start.top > innerHeight)) return null;

  const token = document.createElement('span');
  if (typeof token.animate !== 'function') return null;
  token.className = mobile ? 'calvielle-bag-flight calvielle-bag-flight--compact' : 'calvielle-bag-flight';
  token.setAttribute('aria-hidden', 'true');
  token.setAttribute('popover', 'manual');
  token.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2"><path d="M5 7h14l1 14H4L5 7Z"/><path d="M9 8V5a3 3 0 0 1 6 0v3"/></svg>';

  const endX = end.left + end.width / 2;
  const endY = end.top + end.height / 2;
  const startX = mobile ? Math.max(16, endX - 32) : start.left + start.width / 2;
  const startY = mobile ? Math.min(innerHeight - 16, endY + 72) : start.top + start.height / 2;
  const half = mobile ? 12 : 20;
  token.style.left = `${startX - half}px`;
  token.style.top = `${startY - half}px`;

  // Distinct rapid adds may each confirm success, but never leave two floating
  // tokens on screen. Cancel and remove the previous flight before appending.
  activeFlight?.animation.cancel();
  activeFlight?.token.remove();
  document.body.append(token);
  token.showPopover?.();
  const x = endX - startX;
  const y = endY - startY;
  const animation = token.animate([
    { transform: 'translate(0, 0) scale(.85)', opacity: 0 },
    { transform: `translate(${x * .12}px, ${y * .18}px) scale(${mobile ? '.8' : '1'})`, opacity: mobile ? .8 : .95, offset: .22 },
    { transform: `translate(${x}px, ${y}px) scale(.35)`, opacity: 0 }
  ], { duration: mobile ? 1450 : 1650, easing: 'cubic-bezier(.22,.61,.36,1)' });
  const flight = { token, animation };
  activeFlight = flight;
  return animation.finished.catch(() => {}).finally(() => {
    token.remove();
    if (activeFlight === flight) activeFlight = null;
  });
}
